import { create } from 'zustand';
import { adminApi, publicApi, publicWriteApi } from './api';
import { MOCK_ARTISTS, MOCK_ARTWORKS, MOCK_EXHIBITIONS, MOCK_ART_FAIRS, MOCK_TEAM, MOCK_CATEGORIES, MOCK_DROPSHOP_COLLECTIONS, MOCK_NEWS } from './mock-data';
import type {
  Artwork, Artist, Exhibition, ArtFair, TeamMember,
  Order, OrderStatus, SiteSettings, ActivityLogEntry,
  Category, Subcategory, ShopSettings, DropshopCollection, Review,
  Conversation, Message, NewsletterSubscriber, NewsArticle, FAQ, Partner,
} from '@/types';
import toast from 'react-hot-toast';

// ============================================================================
// LOCAL CACHE — instant load on revisit, API refreshes in background
// ============================================================================
const CACHE_KEY = 'gf-store-cache-v5';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedData {
  ts: number;
  settings: unknown;
  artworks: unknown[];
  categories: unknown[];
  artists: unknown[];
  exhibitions: unknown[];
  artFairs: unknown[];
  teamMembers: unknown[];
  dropshopCollections: unknown[];
  newsArticles: unknown[];
  faqs: unknown[];
  partners: unknown[];
}

function loadCache(): CachedData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as CachedData;
    if (!data.ts || Date.now() - data.ts > CACHE_TTL || !Array.isArray(data.artworks)) return null;
    return data;
  } catch { return null; }
}

function saveCache(state: Record<string, unknown>): void {
  try {
    const lite: CachedData = {
      ts: Date.now(),
      // shopUnlocked is computed per visitor. Cached and replayed to the next load it shows
      // the shop to whoever opens the browser next, then snatches it back when the real
      // answer arrives — so it is dropped before writing.
      settings: (() => {
        const { shopUnlocked: _ignored, ...rest } = (state.settings ?? {}) as Record<string, unknown>;
        return rest;
      })(),
      artworks: (state.artworks as Array<Record<string, unknown>>).map((a) => ({ ...a, images: [] })),
      categories: state.categories as unknown[],
      artists: (state.artists as Array<Record<string, unknown>>).map((a) => ({ ...a, portrait: '' })),
      exhibitions: (state.exhibitions as Array<Record<string, unknown>>).map((e) => ({ ...e, images: [] })),
      artFairs: state.artFairs as unknown[],
      teamMembers: (state.teamMembers as Array<Record<string, unknown>>).map((t) => ({ ...t, image: '' })),
      dropshopCollections: state.dropshopCollections as unknown[],
      newsArticles: state.newsArticles as unknown[],
      faqs: state.faqs as unknown[],
      partners: state.partners as unknown[],
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(lite, (_key, value) => typeof value === 'string' && value.startsWith('data:image/') ? '' : value));
  } catch { /* quota exceeded */ }
}

/**
 * Overwrite only the settings slice of the cache.
 *
 * saveCache runs once per page load, so an admin who changed a setting and reloaded was met
 * by the cached copy of the old value until the API answered — long enough to look like the
 * change had not saved. Rewriting the slice in place keeps the next load's first paint honest.
 */
function patchCachedSettings(settings: unknown): void {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw) as CachedData;
    const { shopUnlocked: _ignored, ...rest } = (settings ?? {}) as Record<string, unknown>;
    data.settings = rest;
    data.ts = Date.now();
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch { /* cache is best-effort */ }
}

/** Deep merge: target provides defaults, source overrides non-undefined values */
function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) && target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
      result[key] = deepMerge(target[key] as Record<string, unknown>, source[key] as Record<string, unknown>);
    } else if (source[key] !== undefined) {
      result[key] = source[key];
    }
  }
  return result;
}

// Clear stale localStorage keys from old versions
try { localStorage.removeItem('gallery-admin-data'); localStorage.removeItem('gallery-admin-v3'); localStorage.removeItem('gallery-admin-v4'); localStorage.removeItem('gallery-admin-v5'); } catch (_) { /* noop */ }

const DEFAULT_SETTINGS: SiteSettings = {
  galleryName: { ka: 'Gallery Fotoatelier', en: 'Gallery Fotoatelier' },
  galleryDescription: { ka: 'თბილისის სახელოვნებო ფოტოგალერეა', en: "Tbilisi's premier fine art photography gallery" },
  socialLinks: {
    instagram: 'https://www.instagram.com',
    facebook: 'https://www.facebook.com',
    behance: 'https://www.behance.net',
    pinterest: 'https://www.pinterest.com',
  },
  footerText: { ka: 'Gallery Fotoatelier, თბილისი', en: 'Gallery Fotoatelier, Tbilisi' },
  shippingThreshold: 50000,
  taxPercentage: 0,
  shopComingSoon: true,
  frameMultipliers: { black: 1.0, white: 1.0, custom: 1.0 },
  heroContent: {
    title: 'FOTOATELIER',
    subtitle: { ka: 'სახელოვნებო ფოტოგალერეა', en: 'Fine Art Photography Gallery' },
  },
  homeAboutSection: {
    label: { ka: 'Gallery Fotoatelier', en: 'Gallery Fotoatelier' },
    title: { ka: 'FINE ART\nPHOTOGRAPHY', en: 'FINE ART\nPHOTOGRAPHY' },
    description: { ka: 'Gallery Fotoatelier არის თბილისის წამყვანი სახელოვნებო ფოტოგალერეა, რომელიც ეძღვნება ქართველი და საერთაშორისო არტისტების თანამედროვე ნამუშევრების ჩვენებას.', en: "Gallery Fotoatelier is Tbilisi's premier fine art photography gallery, dedicated to showcasing exceptional contemporary works from Georgian and international artists." },
    image: '',
  },
  shareButtons: { facebook: true, twitter: true, pinterest: true, whatsapp: true, telegram: true, copyLink: true },
  shopSettings: {
    visibleFilters: { category: true, size: true, rarity: true, priceRange: true },
    defaultSort: 'recommended',
    defaultGridColumns: 3,
  },
  fieldLimits: { title: 200, shortDescription: 500, description: 50000, excerpt: 1000 },
  detailPageTabs: { details: true, artist: true, shipping: true, reviews: true },
  frameOptionsEnabled: { unframed: true, black: true, white: true, custom: true },
  paymentProviders: { bog: true, unipay: true },
  contactInfo: { address: { ka: '', en: '' }, email: '', phone: '', hours: { ka: '', en: '' } },
  menuVisibility: { home: true, shop: true, artists: true, exhibitions: true, artFairs: true, dropshop: true, news: true, contact: true, team: true, faq: true, partners: true, terms: true },
  menuLabels: {},
  deliveryMethodsEnabled: { pickup: true, delivery: true },
};

let settingsRequest = 0;

interface AdminState {
  initialized: boolean;
  /** True once settings have come from the API in this page load, not from the cache. */
  settingsFresh: boolean;
  isAuthenticated: boolean;
  artworks: Artwork[];
  artists: Artist[];
  exhibitions: Exhibition[];
  artFairs: ArtFair[];
  teamMembers: TeamMember[];
  categories: Category[];
  dropshopCollections: DropshopCollection[];
  orders: Order[];
  reviews: Review[];
  conversations: Conversation[];
  newsArticles: NewsArticle[];
  faqs: FAQ[];
  partners: Partner[];
  subscribers: NewsletterSubscriber[];
  settings: SiteSettings;
  activityLog: ActivityLogEntry[];

  initialize: () => Promise<void>;
  initializeAdminData: () => Promise<void>;
  login: (password: string) => Promise<LoginResult>;
  refreshSettings: () => Promise<boolean>;
  logout: () => void;

  addArtwork: (artwork: Omit<Artwork, '_id' | 'slug' | 'viewCount'>) => Promise<Artwork>;
  updateArtwork: (id: string, updates: Partial<Artwork>) => Promise<void>;
  deleteArtwork: (id: string) => void;

  addArtist: (artist: Omit<Artist, '_id' | 'slug' | 'artworkCount'>) => Artist;
  updateArtist: (id: string, updates: Partial<Artist>) => void;
  deleteArtist: (id: string) => void;

  addExhibition: (exhibition: Omit<Exhibition, '_id' | 'slug'>) => Exhibition;
  updateExhibition: (id: string, updates: Partial<Exhibition>) => void;
  deleteExhibition: (id: string) => void;

  addArtFair: (fair: Omit<ArtFair, '_id' | 'slug'>) => ArtFair;
  updateArtFair: (id: string, updates: Partial<ArtFair>) => void;
  deleteArtFair: (id: string) => void;

  addTeamMember: (member: Omit<TeamMember, '_id'>) => TeamMember;
  updateTeamMember: (id: string, updates: Partial<TeamMember>) => void;
  deleteTeamMember: (id: string) => void;
  reorderTeamMembers: (orderedIds: string[]) => void;

  addCategory: (category: Omit<Category, '_id' | 'slug' | 'subcategories'>) => Category;
  updateCategory: (id: string, updates: Partial<Category>) => void;
  deleteCategory: (id: string) => void;
  reorderCategories: (orderedIds: string[]) => void;

  addSubcategory: (categoryId: string, sub: Omit<Subcategory, '_id' | 'slug' | 'parentCategoryId'>) => Subcategory;
  updateSubcategory: (categoryId: string, subId: string, updates: Partial<Subcategory>) => void;
  deleteSubcategory: (categoryId: string, subId: string) => void;

  addDropshopCollection: (data: Omit<DropshopCollection, '_id' | 'slug'>) => DropshopCollection;
  updateDropshopCollection: (id: string, updates: Partial<DropshopCollection>) => void;
  deleteDropshopCollection: (id: string) => void;

  addOrder: (order: Omit<Order, '_id' | 'createdAt' | 'updatedAt'>) => Order;
  updateOrderStatus: (id: string, status: OrderStatus) => void;
  updateOrderTracking: (id: string, trackingId: string, trackingUrl: string) => void;

  addReview: (review: Omit<Review, '_id' | 'createdAt' | 'updatedAt'>) => Review;
  updateReview: (id: string, updates: Partial<Review>) => void;
  deleteReview: (id: string) => void;
  toggleReviewVisibility: (id: string) => void;

  addNewsArticle: (data: Omit<NewsArticle, '_id' | 'slug'>) => NewsArticle;
  updateNewsArticle: (id: string, updates: Partial<NewsArticle>) => void;
  deleteNewsArticle: (id: string) => void;

  addFAQ: (data: Omit<FAQ, '_id'>) => FAQ;
  updateFAQ: (id: string, updates: Partial<FAQ>) => void;
  deleteFAQ: (id: string) => void;
  reorderFAQs: (orderedIds: string[]) => void;

  addPartner: (data: Omit<Partner, '_id'>) => Partner;
  updatePartner: (id: string, updates: Partial<Partner>) => void;
  deletePartner: (id: string) => void;
  reorderPartners: (orderedIds: string[]) => void;

  addSubscriber: (email: string) => boolean;
  removeSubscriber: (id: string) => void;

  addConversation: (conv: Omit<Conversation, '_id' | 'createdAt'>) => Promise<Conversation>;
  addMessageToConversation: (conversationId: string, message: Omit<Message, '_id' | 'createdAt'>) => void;
  markConversationResolved: (conversationId: string) => void;
  markConversationOpen: (conversationId: string) => void;
  markMessagesRead: (conversationId: string, readerType?: 'admin' | 'user') => void;

  updateSettings: (updates: Partial<SiteSettings>) => Promise<void>;
  updateShopSettings: (updates: Partial<ShopSettings>) => Promise<void>;

  getCategories: () => string[];
  getManagedCategories: () => Category[];
  getArtworksByArtist: (artistId: string) => Artwork[];
  resetToMockData: () => void;
  seedDatabase: () => Promise<void>;
}

// Admin password verified server-side via X-Admin-Key header

/** Helper: fire-and-forget API call with error toast. Updates local state immediately for responsiveness. */
/** Why a login attempt failed, so the form can say something useful. */
export interface LoginResult {
  ok: boolean;
  reason?: 'invalid' | 'rate_limited';
}

function apiCall<T>(fn: () => Promise<T>, errorMsg = 'API error'): void {
  fn().catch((err) => {
    console.error(errorMsg, err);
    toast.error(`${errorMsg}: ${err instanceof Error ? err.message : 'Unknown error'}`);
  });
}

export const useAdminStore = create<AdminState>()(
  (set, get) => ({
    initialized: false,
    settingsFresh: false,
    // Require both auth flag AND admin key to be present
    isAuthenticated: localStorage.getItem('gallery-admin-auth') === 'true' && !!localStorage.getItem('gallery-admin-key'),
    artworks: [],
    artists: [],
    exhibitions: [],
    artFairs: [],
    teamMembers: [],
    categories: [],
    dropshopCollections: [],
    orders: [],
    reviews: [],
    conversations: [],
    newsArticles: [],
    faqs: [],
    partners: [],
    subscribers: [],
    settings: DEFAULT_SETTINGS,
    activityLog: [],

    /** Fetch public data from API (for all visitors) — cache-first, then refresh */
    initialize: async (): Promise<void> => {
      if (get().initialized) return;

      // Step 1: Instantly load from cache (0ms — no network wait)
      const cached = loadCache();
      if (cached) {
        set({
          artworks: cached.artworks as Artwork[],
          categories: cached.categories as Category[],
          artists: cached.artists as Artist[],
          exhibitions: cached.exhibitions as Exhibition[],
          artFairs: cached.artFairs as ArtFair[],
          teamMembers: cached.teamMembers as TeamMember[],
          dropshopCollections: cached.dropshopCollections as DropshopCollection[],
          newsArticles: cached.newsArticles as NewsArticle[],
          faqs: cached.faqs as FAQ[],
          partners: cached.partners as Partner[],
          settings: cached.settings ? deepMerge(DEFAULT_SETTINGS as unknown as Record<string, unknown>, cached.settings as Record<string, unknown>) as unknown as SiteSettings : DEFAULT_SETTINGS,
          initialized: true,
        });
        (window as unknown as { __ADMIN_SETTINGS__?: SiteSettings }).__ADMIN_SETTINGS__ = get().settings;
      }

      // Step 2: Fetch fresh data from API (parallel)
      try {
        // Publish independent resources as they arrive. A failing endpoint preserves its
        // last known data instead of blanking unrelated pages or creating mock content.
        const load = async (key: string, fetcher: () => Promise<unknown[]>) => {
          try { const data = await fetcher(); set({ [key]: data }); }
          catch (error) { console.error(`Could not refresh ${key}`, error); }
        };
        const jobs = {
          settings: get().refreshSettings(),
          artworks: load('artworks', publicApi.getArtworksWithImages),
          categories: load('categories', publicApi.getCategories), artists: load('artists', publicApi.getArtists),
          exhibitions: load('exhibitions', publicApi.getExhibitions), artFairs: load('artFairs', publicApi.getArtFairs),
          team: load('teamMembers', publicApi.getTeam), dropshop: load('dropshopCollections', publicApi.getDropshop),
          news: load('newsArticles', publicApi.getNews), faqs: load('faqs', publicApi.getFAQ), partners: load('partners', publicApi.getPartners),
        };
        const segment = window.location.pathname.split('/')[1];
        const routeData = segment === 'exhibitions' || segment === 'exhibition' ? jobs.exhibitions
          : segment === 'art-fairs' || segment === 'art-fair' ? jobs.artFairs
          : segment === 'news' ? jobs.news : segment === 'team' ? jobs.team
          : segment === 'dropshop' ? jobs.dropshop : segment === 'faq' ? jobs.faqs
          : segment === 'partners' ? jobs.partners : segment === 'artists' || segment === 'artist' ? jobs.artists
          : jobs.artworks;
        await Promise.all([jobs.settings, routeData]);
        set({initialized:true});
        await Promise.all(Object.values(jobs));
        saveCache(get() as unknown as Record<string, unknown>);
        if (get().isAuthenticated) void get().initializeAdminData();
      } catch (error) {
        console.error('Could not refresh site content', error);
        set({initialized:true});
      }
    },

    /**
     * Re-asks the server for settings after a sign-in or sign-out.
     *
     * initialize() runs once per page load, so signing in changed nothing until the page was
     * reloaded: shopUnlocked is decided by who is asking, and nobody asked again. A
     * whitelisted member logged in and still saw "Coming Soon" until they refreshed.
     */
    refreshSettings: async (): Promise<boolean> => {
      const requestId = ++settingsRequest;
      try {
        const settings = await publicApi.getSettings();
        if (!settings || requestId !== settingsRequest) return false;
        const merged = deepMerge(DEFAULT_SETTINGS as unknown as Record<string, unknown>, settings as Record<string, unknown>) as unknown as SiteSettings;
        set({ settings: merged, settingsFresh: true });
        (window as unknown as { __ADMIN_SETTINGS__?: SiteSettings }).__ADMIN_SETTINGS__ = merged;
        patchCachedSettings(merged);
        return true;
      } catch { return false; }
    },

    /** Fetch admin-only data (orders, reviews, conversations, subscribers, activity log, ALL entities) */
    initializeAdminData: async (): Promise<void> => {
      try {
        // Test admin auth first — if 401, force logout
        const testArtists = await adminApi.getArtists();
        const [artworks, exhibitions, artFairs, team, categories, dropshop, news, adminFaqs, orders, reviews, conversations, subscribers, settings, activityLog] = await Promise.all([
          adminApi.getArtworks().catch(() => null),
          adminApi.getExhibitions().catch(() => null),
          adminApi.getArtFairs().catch(() => null),
          adminApi.getTeam().catch(() => null),
          adminApi.getCategories().catch(() => null),
          adminApi.getDropshop().catch(() => null),
          adminApi.getNews().catch(() => null),
          adminApi.getFAQ().catch(() => null),
          adminApi.getOrders().catch(() => []),
          adminApi.getReviews().catch(() => []),
          adminApi.getConversations().catch(() => []),
          adminApi.getSubscribers().catch(() => []),
          adminApi.getSettings().catch(() => null),
          adminApi.getActivityLog().catch(() => []),
        ]);
        set({
          artists: testArtists as Artist[],
          ...(artworks ? { artworks: artworks as Artwork[] } : {}),
          ...(exhibitions ? { exhibitions: exhibitions as Exhibition[] } : {}),
          ...(artFairs ? { artFairs: artFairs as ArtFair[] } : {}),
          ...(team ? { teamMembers: team as TeamMember[] } : {}),
          ...(categories ? { categories: categories as Category[] } : {}),
          ...(dropshop ? { dropshopCollections: dropshop as DropshopCollection[] } : {}),
          ...(news ? { newsArticles: news as NewsArticle[] } : {}),
          ...(adminFaqs ? { faqs: adminFaqs as FAQ[] } : {}),
          orders: orders as Order[],
          reviews: reviews as Review[],
          conversations: conversations as Conversation[],
          subscribers: subscribers as NewsletterSubscriber[],
          ...(settings ? { settings: deepMerge(DEFAULT_SETTINGS as unknown as Record<string, unknown>, settings as Record<string, unknown>) as unknown as SiteSettings } : {}),
          activityLog: activityLog as ActivityLogEntry[],
        });
      } catch (err: unknown) {
        console.error('Failed to fetch admin data', err);
        // If 401 unauthorized, force admin logout so they see the login screen
        if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 401) {
          localStorage.removeItem('gallery-admin-auth');
          localStorage.removeItem('gallery-admin-key');
          set({ isAuthenticated: false });
          toast.error('Admin session expired. Please log in again.');
        }
      }
    },

    /**
     * Confirms the key with the server before granting access.
     *
     * This used to store the key, flip isAuthenticated and return true unconditionally,
     * leaving verification to a later request — so a wrong key opened the panel, then a
     * background 401 wiped it, which read as the screen flickering rather than as a
     * rejected password. The API itself was never exposed; adminAuth refuses every
     * endpoint. What was missing was an answer before the door opened.
     */
    login: async (password: string): Promise<LoginResult> => {
      const key = password.trim();
      if (!key) return { ok: false, reason: 'invalid' };

      // The client reads the key from storage, so it has to be written before the check —
      // and removed again if the check fails, or a bad key would linger.
      localStorage.setItem('gallery-admin-key', key);
      try {
        await adminApi.verifyKey();
      } catch (err: unknown) {
        localStorage.removeItem('gallery-admin-key');
        const status = err && typeof err === 'object' && 'status' in err ? (err as { status: number }).status : 0;
        // 429 means the brute-force throttle is holding, not that the key is wrong —
        // telling someone their password is wrong when it is not wastes a lot of time.
        return { ok: false, reason: status === 429 ? 'rate_limited' : 'invalid' };
      }

      localStorage.setItem('gallery-admin-auth', 'true');
      set({ isAuthenticated: true });
      void get().initializeAdminData();
      return { ok: true };
    },

    logout: (): void => {
      localStorage.removeItem('gallery-admin-auth');
      localStorage.removeItem('gallery-admin-key');
      set({ isAuthenticated: false });
    },

    // === ARTWORK CRUD ===
    addArtwork: async (data): Promise<Artwork> => {
      const artwork = await adminApi.createArtwork(data) as Artwork;
      set(s => ({artworks:[artwork,...s.artworks]}));
      void adminApi.getArtists().then(artists=>set({artists:artists as Artist[]})).catch(()=>{});
      return artwork;
    },
    updateArtwork: async (id, updates): Promise<void> => {
      const artwork = await adminApi.updateArtwork(id, updates) as Artwork;
      set(s=>({artworks:s.artworks.map(a=>a._id===id?artwork:a)}));
      void adminApi.getArtists().then(artists=>set({artists:artists as Artist[]})).catch(()=>{});
    },

    deleteArtwork: (id): void => {
      set((s) => ({ artworks: s.artworks.filter((a) => a._id !== id) }));
      apiCall(async () => {
        await adminApi.deleteArtwork(id);
        const artists = await adminApi.getArtists() as Artist[];
        set({ artists });
      }, 'Failed to delete artwork');
    },

    // === ARTIST CRUD ===
    addArtist: (data): Artist => {
      const tempArtist: Artist = { ...data, _id: 'temp_' + Date.now(), slug: 'temp', artworkCount: 0 };
      set((s) => ({ artists: [...s.artists, tempArtist] }));

      apiCall(async () => {
        const artist = await adminApi.createArtist(data) as Artist;
        set((s) => ({ artists: s.artists.map((a) => a._id === tempArtist._id ? artist : a) }));
      }, 'Failed to create artist');
      return tempArtist;
    },

    updateArtist: (id, updates): void => {
      set((s) => ({ artists: s.artists.map((a) => (a._id === id ? { ...a, ...updates } : a)) }));
      apiCall(() => adminApi.updateArtist(id, updates), 'Failed to update artist');
    },

    deleteArtist: (id): void => {
      set((s) => ({ artists: s.artists.filter((a) => a._id !== id) }));
      apiCall(() => adminApi.deleteArtist(id), 'Failed to delete artist');
    },

    // === EXHIBITION CRUD ===
    addExhibition: (data): Exhibition => {
      const temp: Exhibition = { ...data, _id: 'temp_' + Date.now(), slug: 'temp' };
      set((s) => ({ exhibitions: [...s.exhibitions, temp] }));

      apiCall(async () => {
        const exhibition = await adminApi.createExhibition(data) as Exhibition;
        set((s) => ({ exhibitions: s.exhibitions.map((e) => e._id === temp._id ? exhibition : e) }));
      }, 'Failed to create exhibition');
      return temp;
    },

    updateExhibition: (id, updates): void => {
      set((s) => ({ exhibitions: s.exhibitions.map((e) => (e._id === id ? { ...e, ...updates } : e)) }));
      apiCall(() => adminApi.updateExhibition(id, updates), 'Failed to update exhibition');
    },

    deleteExhibition: (id): void => {
      set((s) => ({ exhibitions: s.exhibitions.filter((e) => e._id !== id) }));
      apiCall(() => adminApi.deleteExhibition(id), 'Failed to delete exhibition');
    },

    // === ART FAIR CRUD ===
    addArtFair: (data): ArtFair => {
      const temp: ArtFair = { ...data, _id: 'temp_' + Date.now(), slug: 'temp' };
      set((s) => ({ artFairs: [...s.artFairs, temp] }));

      apiCall(async () => {
        const fair = await adminApi.createArtFair(data) as ArtFair;
        set((s) => ({ artFairs: s.artFairs.map((f) => f._id === temp._id ? fair : f) }));
      }, 'Failed to create art fair');
      return temp;
    },

    updateArtFair: (id, updates): void => {
      set((s) => ({ artFairs: s.artFairs.map((f) => (f._id === id ? { ...f, ...updates } : f)) }));
      apiCall(() => adminApi.updateArtFair(id, updates), 'Failed to update art fair');
    },

    deleteArtFair: (id): void => {
      set((s) => ({ artFairs: s.artFairs.filter((f) => f._id !== id) }));
      apiCall(() => adminApi.deleteArtFair(id), 'Failed to delete art fair');
    },

    // === TEAM CRUD ===
    addTeamMember: (data): TeamMember => {
      const temp: TeamMember = { ...data, _id: 'temp_' + Date.now() };
      set((s) => ({ teamMembers: [...s.teamMembers, temp] }));

      apiCall(async () => {
        const member = await adminApi.createTeamMember(data) as TeamMember;
        set((s) => ({ teamMembers: s.teamMembers.map((m) => m._id === temp._id ? member : m) }));
      }, 'Failed to create team member');
      return temp;
    },

    updateTeamMember: (id, updates): void => {
      set((s) => ({ teamMembers: s.teamMembers.map((m) => (m._id === id ? { ...m, ...updates } : m)) }));
      apiCall(() => adminApi.updateTeamMember(id, updates), 'Failed to update team member');
    },

    deleteTeamMember: (id): void => {
      set((s) => ({ teamMembers: s.teamMembers.filter((m) => m._id !== id) }));
      apiCall(() => adminApi.deleteTeamMember(id), 'Failed to delete team member');
    },

    reorderTeamMembers: (orderedIds): void => {
      set((s) => ({
        teamMembers: s.teamMembers
          .map((m) => ({ ...m, order: orderedIds.indexOf(m._id) + 1 }))
          .sort((a, b) => a.order - b.order),
      }));
      apiCall(() => adminApi.reorderTeam(orderedIds), 'Failed to reorder team');
    },

    // === CATEGORY CRUD ===
    addCategory: (data): Category => {
      const temp: Category = { ...data, _id: 'temp_' + Date.now(), slug: 'temp', subcategories: [] };
      set((s) => ({ categories: [...s.categories, temp] }));

      apiCall(async () => {
        const category = await adminApi.createCategory(data) as Category;
        set((s) => ({ categories: s.categories.map((c) => c._id === temp._id ? category : c) }));
      }, 'Failed to create category');
      return temp;
    },

    updateCategory: (id, updates): void => {
      set((s) => ({ categories: s.categories.map((c) => (c._id === id ? { ...c, ...updates } : c)) }));
      apiCall(() => adminApi.updateCategory(id, updates), 'Failed to update category');
    },

    deleteCategory: (id): void => {
      set((s) => ({
        categories: s.categories.filter((c) => c._id !== id),
        artworks: s.artworks.map((a) => a.categoryId === id ? { ...a, categoryId: undefined, subcategoryId: undefined } : a),
      }));
      apiCall(() => adminApi.deleteCategory(id), 'Failed to delete category');
    },

    reorderCategories: (orderedIds): void => {
      set((s) => ({
        categories: s.categories
          .map((c) => ({ ...c, displayOrder: orderedIds.indexOf(c._id) + 1 }))
          .sort((a, b) => a.displayOrder - b.displayOrder),
      }));
      apiCall(() => adminApi.reorderCategories(orderedIds), 'Failed to reorder categories');
    },

    // === SUBCATEGORY CRUD ===
    addSubcategory: (categoryId, data): Subcategory => {
      const sub: Subcategory = { ...data, _id: 'temp_' + Date.now(), slug: 'temp', parentCategoryId: categoryId };
      set((s) => ({
        categories: s.categories.map((c) =>
          c._id === categoryId ? { ...c, subcategories: [...c.subcategories, sub] } : c,
        ),
      }));

      apiCall(async () => {
        const updated = await adminApi.addSubcategory(categoryId, data) as Category;
        set((s) => ({ categories: s.categories.map((c) => c._id === categoryId ? updated : c) }));
      }, 'Failed to add subcategory');
      return sub;
    },

    updateSubcategory: (categoryId, subId, updates): void => {
      set((s) => ({
        categories: s.categories.map((c) =>
          c._id === categoryId
            ? { ...c, subcategories: c.subcategories.map((sc) => sc._id === subId ? { ...sc, ...updates } : sc) }
            : c,
        ),
      }));
      apiCall(async () => {
        const updated = await adminApi.updateSubcategory(categoryId, subId, updates) as Category;
        set((s) => ({ categories: s.categories.map((c) => c._id === categoryId ? updated : c) }));
      }, 'Failed to update subcategory');
    },

    deleteSubcategory: (categoryId, subId): void => {
      set((s) => ({
        categories: s.categories.map((c) =>
          c._id === categoryId
            ? { ...c, subcategories: c.subcategories.filter((sc) => sc._id !== subId) }
            : c,
        ),
        artworks: s.artworks.map((a) => a.subcategoryId === subId ? { ...a, subcategoryId: undefined } : a),
      }));
      apiCall(() => adminApi.deleteSubcategory(categoryId, subId), 'Failed to delete subcategory');
    },

    // === DROPSHOP CRUD ===
    addDropshopCollection: (data): DropshopCollection => {
      const temp: DropshopCollection = { ...data, _id: 'temp_' + Date.now(), slug: 'temp' };
      set((s) => ({ dropshopCollections: [...s.dropshopCollections, temp] }));

      apiCall(async () => {
        const collection = await adminApi.createDropshop(data) as DropshopCollection;
        set((s) => ({ dropshopCollections: s.dropshopCollections.map((d) => d._id === temp._id ? collection : d) }));
      }, 'Failed to create dropshop collection');
      return temp;
    },

    updateDropshopCollection: (id, updates): void => {
      set((s) => ({ dropshopCollections: s.dropshopCollections.map((d) => (d._id === id ? { ...d, ...updates } : d)) }));
      apiCall(() => adminApi.updateDropshop(id, updates), 'Failed to update dropshop collection');
    },

    deleteDropshopCollection: (id): void => {
      set((s) => ({ dropshopCollections: s.dropshopCollections.filter((d) => d._id !== id) }));
      apiCall(() => adminApi.deleteDropshop(id), 'Failed to delete dropshop collection');
    },

    // === ORDER ===
    addOrder: (data): Order => {
      const now = new Date().toISOString();
      const temp: Order = { ...data, _id: 'temp_' + Date.now(), createdAt: now, updatedAt: now };
      set((s) => ({ orders: [...s.orders, temp] }));

      apiCall(async () => {
        const order = await publicWriteApi.createOrder(data) as Order;
        set((s) => ({ orders: s.orders.map((o) => o._id === temp._id ? order : o) }));
      }, 'Failed to create order');
      return temp;
    },

    updateOrderStatus: (id, status): void => {
      set((s) => ({
        orders: s.orders.map((o) => o._id === id ? { ...o, status, updatedAt: new Date().toISOString() } : o),
      }));
      apiCall(() => adminApi.updateOrderStatus(id, status), 'Failed to update order status');
    },

    updateOrderTracking: (id, trackingId, trackingUrl): void => {
      set((s) => ({
        orders: s.orders.map((o) => o._id === id ? { ...o, trackingId, trackingUrl, updatedAt: new Date().toISOString() } : o),
      }));
      apiCall(() => adminApi.updateOrderTracking(id, trackingId, trackingUrl), 'Failed to update tracking');
    },

    // === REVIEWS CRUD ===
    addReview: (data): Review => {
      const now = new Date().toISOString();
      const temp: Review = { ...data, _id: 'temp_' + Date.now(), createdAt: now, updatedAt: now };
      set((s) => ({ reviews: [...s.reviews, temp] }));

      apiCall(async () => {
        const review = await adminApi.createReview(data) as Review;
        set((s) => ({ reviews: s.reviews.map((r) => r._id === temp._id ? review : r) }));
      }, 'Failed to create review');
      return temp;
    },

    updateReview: (id, updates): void => {
      set((s) => ({ reviews: s.reviews.map((r) => (r._id === id ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r)) }));
      apiCall(() => adminApi.updateReview(id, updates), 'Failed to update review');
    },

    deleteReview: (id): void => {
      set((s) => ({ reviews: s.reviews.filter((r) => r._id !== id) }));
      apiCall(() => adminApi.deleteReview(id), 'Failed to delete review');
    },

    toggleReviewVisibility: (id): void => {
      set((s) => ({
        reviews: s.reviews.map((r) => (r._id === id ? { ...r, isHidden: !r.isHidden, updatedAt: new Date().toISOString() } : r)),
      }));
      apiCall(() => adminApi.toggleReviewVisibility(id), 'Failed to toggle review visibility');
    },

    // === NEWS ARTICLES CRUD ===
    addNewsArticle: (data): NewsArticle => {
      const temp: NewsArticle = { ...data, _id: 'temp_' + Date.now(), slug: 'temp' };
      set((s) => ({ newsArticles: [...s.newsArticles, temp] }));

      apiCall(async () => {
        const article = await adminApi.createNews(data) as NewsArticle;
        set((s) => ({ newsArticles: s.newsArticles.map((n) => n._id === temp._id ? article : n) }));
      }, 'Failed to create news article');
      return temp;
    },

    updateNewsArticle: (id, updates): void => {
      set((s) => ({ newsArticles: s.newsArticles.map((n) => (n._id === id ? { ...n, ...updates } : n)) }));
      apiCall(() => adminApi.updateNews(id, updates), 'Failed to update news article');
    },

    deleteNewsArticle: (id): void => {
      set((s) => ({ newsArticles: s.newsArticles.filter((n) => n._id !== id) }));
      apiCall(() => adminApi.deleteNews(id), 'Failed to delete news article');
    },

    // === FAQ ===
    addFAQ: (data): FAQ => {
      const temp: FAQ = { ...data, _id: 'temp_' + Date.now() };
      set((s) => ({ faqs: [...s.faqs, temp] }));

      apiCall(async () => {
        const faq = await adminApi.createFAQ(data) as FAQ;
        set((s) => ({ faqs: s.faqs.map((f) => f._id === temp._id ? faq : f) }));
      }, 'Failed to create FAQ');
      return temp;
    },

    updateFAQ: (id, updates): void => {
      set((s) => ({ faqs: s.faqs.map((f) => (f._id === id ? { ...f, ...updates } : f)) }));
      apiCall(() => adminApi.updateFAQ(id, updates), 'Failed to update FAQ');
    },

    deleteFAQ: (id): void => {
      set((s) => ({ faqs: s.faqs.filter((f) => f._id !== id) }));
      apiCall(() => adminApi.deleteFAQ(id), 'Failed to delete FAQ');
    },

    reorderFAQs: (orderedIds): void => {
      set((s) => {
        const faqs = orderedIds
          .map((id, i) => {
            const faq = s.faqs.find((f) => f._id === id);
            return faq ? { ...faq, displayOrder: i } : null;
          })
          .filter((f): f is FAQ => f !== null);
        return { faqs };
      });
      apiCall(() => adminApi.reorderFAQ(orderedIds), 'Failed to reorder FAQ');
    },

    // === PARTNERS ===
    addPartner: (data): Partner => {
      const temp: Partner = { ...data, _id: 'temp_' + Date.now() } as Partner;
      set((s) => ({ partners: [...s.partners, temp] }));
      apiCall(async () => {
        const partner = await adminApi.createPartner(data) as Partner;
        set((s) => ({ partners: s.partners.map((p) => p._id === temp._id ? partner : p) }));
      }, 'Failed to create partner');
      return temp;
    },

    updatePartner: (id, updates): void => {
      set((s) => ({ partners: s.partners.map((p) => (p._id === id ? { ...p, ...updates } : p)) }));
      apiCall(() => adminApi.updatePartner(id, updates), 'Failed to update partner');
    },

    deletePartner: (id): void => {
      set((s) => ({ partners: s.partners.filter((p) => p._id !== id) }));
      apiCall(() => adminApi.deletePartner(id), 'Failed to delete partner');
    },

    reorderPartners: (orderedIds): void => {
      set((s) => {
        const partners = orderedIds
          .map((id, i) => {
            const p = s.partners.find((x) => x._id === id);
            return p ? { ...p, displayOrder: i } : null;
          })
          .filter((p): p is Partner => p !== null);
        return { partners };
      });
      apiCall(() => adminApi.reorderPartners(orderedIds), 'Failed to reorder partners');
    },

    // === NEWSLETTER SUBSCRIBERS ===
    addSubscriber: (email): boolean => {
      const state = get();
      const normalized = email.toLowerCase().trim();
      if (state.subscribers.some((s) => s.email === normalized)) return false;
      const now = new Date().toISOString();
      const temp: NewsletterSubscriber = { _id: 'temp_' + Date.now(), email: normalized, subscribedAt: now };
      set((s) => ({ subscribers: [...s.subscribers, temp] }));

      apiCall(async () => {
        const subscriber = await publicWriteApi.addSubscriber(normalized) as NewsletterSubscriber;
        set((s) => ({ subscribers: s.subscribers.map((sub) => sub._id === temp._id ? subscriber : sub) }));
      }, 'Failed to add subscriber');
      return true;
    },

    removeSubscriber: (id): void => {
      set((s) => ({ subscribers: s.subscribers.filter((sub) => sub._id !== id) }));
      apiCall(() => adminApi.removeSubscriber(id), 'Failed to remove subscriber');
    },

    // === CONVERSATIONS / INBOX ===
    addConversation: async (data): Promise<Conversation> => {
      const conv = await publicWriteApi.createConversation(data) as Conversation;
      set(s => ({ conversations: [conv, ...s.conversations] }));
      return conv;
    },

    addMessageToConversation: (conversationId, msgData): void => {
      const now = new Date().toISOString();
      const tempMsg: Message = { ...msgData, _id: 'temp_' + Date.now(), createdAt: now };
      set((s) => ({
        conversations: s.conversations.map((c) => {
          if (c._id !== conversationId) return c;
          return { ...c, messages: [...c.messages, tempMsg], lastMessageAt: now };
        }),
      }));

      apiCall(async () => {
        // Use admin API if authenticated as admin, otherwise public API
        const isAdmin = get().isAuthenticated;
        const conv = isAdmin
          ? await adminApi.addMessage(conversationId, msgData) as Conversation
          : await publicWriteApi.addMessage(conversationId, msgData) as Conversation;
        set((s) => ({ conversations: s.conversations.map((c) => c._id === conversationId ? conv : c) }));
      }, 'Failed to send message');
    },

    markConversationResolved: (conversationId): void => {
      set((s) => ({
        conversations: s.conversations.map((c) => c._id === conversationId ? { ...c, status: 'resolved' as const } : c),
      }));
      apiCall(() => adminApi.resolveConversation(conversationId), 'Failed to resolve conversation');
    },

    markConversationOpen: (conversationId): void => {
      set((s) => ({
        conversations: s.conversations.map((c) => c._id === conversationId ? { ...c, status: 'open' as const } : c),
      }));
      apiCall(() => adminApi.reopenConversation(conversationId), 'Failed to reopen conversation');
    },

    markMessagesRead: (conversationId, readerType): void => {
      set((s) => ({
        conversations: s.conversations.map((c) => {
          if (c._id !== conversationId) return c;
          return {
            ...c,
            messages: c.messages.map((m) => {
              if (readerType === 'admin' && m.senderType !== 'admin') return { ...m, read: true };
              if (readerType === 'user' && m.senderType === 'admin') return { ...m, read: true };
              if (!readerType) return { ...m, read: true };
              return m;
            }),
          };
        }),
      }));
      // Only call admin API if authenticated as admin; skip for regular users
      if (get().isAuthenticated) {
        apiCall(() => adminApi.markRead(conversationId), 'Failed to mark messages read');
      }
    },

    // === SETTINGS ===
    updateSettings: async (updates): Promise<void> => {
      ++settingsRequest; // Invalidate reads started before this save.
      const saved = await adminApi.updateSettings(updates);
      ++settingsRequest;
      const merged = deepMerge(get().settings as unknown as Record<string, unknown>, saved as Record<string, unknown>) as unknown as SiteSettings;
      set({ settings: merged, settingsFresh: true });
      patchCachedSettings(merged);
      await get().refreshSettings();
    },

    updateShopSettings: async (updates): Promise<void> => {
      await get().updateSettings({ shopSettings: { ...get().settings.shopSettings, ...updates } });
    },

    getCategories: (): string[] => {
      const cats = new Set(get().artworks.map((a) => a.category));
      return ['All', ...Array.from(cats)];
    },

    getManagedCategories: (): Category[] => {
      return get().categories
        .filter((c) => c.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder);
    },

    getArtworksByArtist: (artistId): Artwork[] => {
      return get().artworks.filter((a) => {
        const aId = typeof a.artist === 'string' ? a.artist : (a.artist as Artist)?._id;
        return aId === artistId;
      });
    },

    resetToMockData: (): void => {
      set({
        artworks: MOCK_ARTWORKS,
        artists: MOCK_ARTISTS,
        exhibitions: MOCK_EXHIBITIONS,
        artFairs: MOCK_ART_FAIRS,
        teamMembers: MOCK_TEAM,
        categories: MOCK_CATEGORIES,
        dropshopCollections: MOCK_DROPSHOP_COLLECTIONS,
        orders: [],
        reviews: [],
        newsArticles: MOCK_NEWS,
        subscribers: [],
        settings: DEFAULT_SETTINGS,
        activityLog: [],
      });
    },

    seedDatabase: async (): Promise<void> => {
      try {
        await adminApi.seed({
          artists: MOCK_ARTISTS,
          artworks: MOCK_ARTWORKS,
          exhibitions: MOCK_EXHIBITIONS,
          artFairs: MOCK_ART_FAIRS,
          teamMembers: MOCK_TEAM,
          categories: MOCK_CATEGORIES,
          dropshopCollections: MOCK_DROPSHOP_COLLECTIONS,
          newsArticles: MOCK_NEWS,
          settings: DEFAULT_SETTINGS,
        });
        toast.success('Database seeded with initial data');
        // Re-fetch everything
        await get().initialize();
      } catch (err) {
        toast.error(`Failed to seed database: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    },
  }),
);
